import { SystemSpecificationService } from './../../service/system-specification.service';
import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DeviceManagmentService } from '@service/device-managment.service';
import { FcuUnitServiece } from '@service/fcu-unit.service';
import { OpsUnitService } from '@service/ops-unit.service';
import { SlotService } from '@service/slot.service';
import { VesselEquipmentService } from '@service/vessel-equipment.service';
import { VesselService } from '@service/vessel.service';
import { DefaultDevices, Device, VesselEquipment } from 'icreate/common/models/device';
import { FcuUnit } from 'icreate/common/models/fcu-unit';
import { FuUnit } from 'icreate/common/models/fu-unit';
import { Vessel } from 'icreate/common/models/vessel';
import { from, lastValueFrom, Observable, Subject, take, takeUntil } from 'rxjs';
import { HardwareSpecificationsComponent } from './hardware-specifications/hardware-specifications.component';
import {
    FuUnitWithFcuIndex,
    HardwareSpecification,
    OthersSpecification,
    PT100Type,
    ResistanceType,
    SoftwareSpecification,
    SoftwareSpecificationApi,
    SystemSpecification,
} from 'icreate/common/models/system-specification';
import { OpsUnit } from 'icreate/common/models/ops-unit';
import { Slot } from 'icreate/common/models/slot';
import { GraphType } from 'icreate/common/models/graphs';
import { commonLanguages, MasterClockType } from 'icreate/common/models/common';
import { SoftwareSpecificationsComponent } from './software-specifications/software-specifications.component';
import { MessageDialogService } from '@service/message-dialog.service';
import { SystemSpecificationSocketService } from '@service/system-specification-socket.ts.service';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { environment } from '@environments/environment';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';
import { EnvironmentService } from '@service/environment.service';

enum SpecificationTab {
    UNDEFINED = 'undefined',
    HARDWARE = 'hardware',
    SOFTWARE = 'software',
    OTHERS = 'others',
}

@Component({
    selector: 'app-system-specification',
    templateUrl: './system-specification.component.html',
    styleUrl: './system-specification.component.css',
    standalone: false,
})
export class SystemSpecificationComponent {
    readonly HARDWARE_TAB = SpecificationTab.HARDWARE;
    readonly SOFTWARE_TAB = SpecificationTab.SOFTWARE;
    readonly OTHERS_TAB = SpecificationTab.OTHERS;

    @ViewChild('hardwareSettings') hardwareSettings: HardwareSpecificationsComponent;
    @ViewChild('softwareSettings') softwareSettings: SoftwareSpecificationsComponent;

    @ViewChild('tabsContainer') tabsContainer: ElementRef;

    vessel: Vessel;
    userCanEdit: boolean = false;
    isLoading: boolean = false;
    selectedTab: SpecificationTab = SpecificationTab.HARDWARE;

    hardwareSpecification: HardwareSpecification;
    softwareSpecification: SoftwareSpecification;
    othersSpecification: OthersSpecification;

    private initialSystemSpecifications: SystemSpecification;
    private destroy$: Subject<void> = new Subject();
    private env: RuntimeEnvironments;

    @HostListener('window:beforeunload', ['$event'])
    async beforeUnload(event) {
        if (this.isSaveEnabled) {
            event.preventDefault();
        }
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private vesselService: VesselService,
        private deviceManagmentService: DeviceManagmentService,
        private opsUnitService: OpsUnitService,
        private slotService: SlotService,
        private fcuUnitService: FcuUnitServiece,
        private vesselEquipmentService: VesselEquipmentService,
        private systemSpecificationService: SystemSpecificationService,
        private systemSpecificationSocketService: SystemSpecificationSocketService,
        private messageDialogService: MessageDialogService,
        private dialog: MatDialog,
        private environmentService: EnvironmentService
    ) {
        this.env = this.environmentService.getEnvironment();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        if (this.env == RuntimeEnvironments.ELECTRON) {
            await this.router.navigateByUrl('/home');
            return;
        }
        try {
            const params = this.route.snapshot.params;
            const vesselId = params['vesselId'];
            const currentVessel: any = await this.vesselService.getVessel(vesselId);
            if (!currentVessel) {
                this.router.navigateByUrl('home');
                return;
            }

            this.vesselService.setActiveVessel(currentVessel);
            this.vessel = currentVessel;

            const [
                deviceList,
                opsUnits,
                slots,
                fcuUnits,
                vesselEquipment,
                systemSpecification,
                softwareSpecificationApi,
            ] = await Promise.all([
                this.deviceManagmentService.getAll(),
                this.opsUnitService.getList(this.vessel.id),
                this.slotService.getByVesselId(this.vessel.id),
                this.fcuUnitService.getFcuWithFuAndCeu(this.vessel.id),
                this.vesselEquipmentService.getAll(this.vessel.iAPIVesselId),
                this.systemSpecificationService.getSystemSpecification(this.vessel.id),
                this.systemSpecificationService.getSoftwareSpecification(this.vessel.id),
            ]);

            if (systemSpecification) this.initialSystemSpecifications = structuredClone(systemSpecification);
            else
                this.initialSystemSpecifications = {
                    vesselId: this.vessel.id,
                    keyboards: 0,
                    trackballs: 0,
                    hub: 0,
                    items: '',
                    notes: '',
                };

            this.prepareHardwareSettings(deviceList, opsUnits, slots, fcuUnits, vesselEquipment, systemSpecification);
            this.prepareSoftwareSettings(softwareSpecificationApi);
            this.prepareOthersSettings(systemSpecification);
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }

        this.systemSpecificationSocketService
            .specificationsUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((updatedSetting: SystemSpecification) => {
                if (updatedSetting.vesselId === this.vessel.id) {
                    this.initialSystemSpecifications = structuredClone(updatedSetting);
                    this.hardwareSpecification.Keyboard.qty = updatedSetting?.keyboards ?? 0;
                    this.hardwareSpecification.Trackball.qty = updatedSetting?.trackballs ?? 0;
                    this.hardwareSpecification.HUB.qty = updatedSetting?.hub ?? 0;
                    this.othersSpecification.items = updatedSetting?.items ?? '';
                    this.othersSpecification.notes = updatedSetting?.notes ?? '';
                    this.showMessageDialog('dlg.changes-saved');
                }
            });

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroy$)).subscribe(canEdit => {
            this.userCanEdit = canEdit;
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async selectTab(tab: SpecificationTab) {
        if (this.selectedTab == this.HARDWARE_TAB || this.selectedTab == this.OTHERS_TAB) {
            const shouldChange = await this.shouldLeaveConfirmation();
            if (!shouldChange) return;
        }
        this.selectedTab = tab;
    }

    isTabSelected(tab: SpecificationTab) {
        return this.selectedTab == tab;
    }

    async backButtonClick() {
        const shouldLeave = await this.shouldLeaveConfirmation();
        if (!shouldLeave) return;
        await this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
    }

    async save() {
        if (!this.isSaveEnabled) return;
        try {
            const setting: SystemSpecification = {
                id: this.initialSystemSpecifications?.id,
                vesselId: this.vessel.id,
                keyboards: this.hardwareSpecification.Keyboard.qty,
                trackballs: this.hardwareSpecification.Trackball.qty,
                hub: this.hardwareSpecification.HUB.qty,
                items: this.othersSpecification.items,
                notes: this.othersSpecification.notes,
            };
            await this.systemSpecificationService.save(setting);
        } catch (err) {
            console.error(err);
        }
    }

    cancel() {
        this.hardwareSpecification.Keyboard.qty = this.initialSystemSpecifications?.keyboards ?? 0;
        this.hardwareSpecification.Trackball.qty = this.initialSystemSpecifications?.trackballs ?? 0;
        this.hardwareSpecification.HUB.qty = this.initialSystemSpecifications?.hub ?? 0;
        this.othersSpecification.items = this.initialSystemSpecifications?.items ?? '';
        this.othersSpecification.notes = this.initialSystemSpecifications?.notes ?? '';
    }

    prepareHardwareSettings(
        deviceList: Device[],
        opsUnits: OpsUnit[],
        slots: Slot[],
        fcuUnits,
        vesselEquipment: VesselEquipment[],
        systemSpecification: SystemSpecification
    ) {
        const fuUnits = this.prepareFuList(fcuUnits);

        const deviceListByName = new Map(deviceList.map(d => [d.name, d]));
        const equipmentByName = new Map<string, typeof vesselEquipment>();
        for (const equipment of vesselEquipment) {
            const list = equipmentByName.get(equipment.name) ?? [];
            list.push(equipment);
            equipmentByName.set(equipment.name, list);
        }

        const getModelName = (deviceName: string, designCode?: string | number): string | null => {
            const equipmentList = equipmentByName.get(deviceName) ?? [];
            const equipment =
                designCode === undefined ? equipmentList[0] : equipmentList.find(e => e.designCode == designCode);
            if (!equipment?.equipmentModelId) return '-';

            const device = deviceListByName.get(deviceName);
            return device?.models.find(m => m.id == equipment.equipmentModelId)?.name ?? '-';
        };

        this.hardwareSpecification = new HardwareSpecification();

        const simpleDevices: Array<[string, number]> = [
            [DefaultDevices.FCU, fuUnits.length > 0 ? fcuUnits.length : 0],
            [DefaultDevices.HUB, systemSpecification?.hub ?? 0],
            [DefaultDevices.IEDGE, 1],
            [DefaultDevices.KEYBOARD, systemSpecification?.keyboards ?? 0],
            [DefaultDevices.TRACKBALL, systemSpecification?.trackballs ?? 0],
        ];

        for (const [deviceName, qty] of simpleDevices) {
            const model = getModelName(deviceName);
            this.hardwareSpecification[deviceName] = { model, qty };
        }

        for (const unit of opsUnits) {
            if (unit.index > 16) continue;
            const model = getModelName(DefaultDevices.OPS, unit.id);
            this.hardwareSpecification[DefaultDevices.OPS].push({ ...unit, model });
        }

        this.hardwareSpecification.OPS.sort((a, b) => a.index - b.index);

        const fuModelsObject: { [model: string]: string[] } = {};
        for (const unit of fuUnits) {
            const model = getModelName(DefaultDevices.FU, unit.id);
            if (fuModelsObject[model]) {
                fuModelsObject[model] = [...fuModelsObject[model], unit.location];
            } else {
                fuModelsObject[model] = [unit.location];
            }
        }

        const uniqueFuModels = Object.keys(fuModelsObject);
        for (const model of uniqueFuModels) {
            const locations = fuModelsObject[model];
            this.hardwareSpecification[DefaultDevices.FU].push({
                model,
                locations: locations,
                qty: locations.length,
            });
        }

        const has2Wire = slots.some(slot => slot.ioType === 'AI (Pt100 2-wire)');
        const has3Wire = slots.some(slot => slot.ioType === 'AI (Pt100 3-wire)');
        if (has2Wire || has3Wire) {
            this.hardwareSpecification.PT100 = {
                wiring: has3Wire ? PT100Type.WIRE_3 : PT100Type.WIRE_2,
                resitance: ResistanceType.JIS,
            };
        }
    }

    prepareSoftwareSettings(softwareSpecificationApi: SoftwareSpecificationApi): void {
        this.softwareSpecification = new SoftwareSpecification();

        if (!softwareSpecificationApi.common) {
            softwareSpecificationApi.common = {
                id: '',
                vesselId: this.vessel.id,
                language: commonLanguages.EN,
                masterClockType: MasterClockType.MasterPulse,
                UtcEnable: 0,
                japanCoastGuard: 0,
            };
        }
        this.softwareSpecification.display.mimics = softwareSpecificationApi.hasMimics;
        this.softwareSpecification.display.language = softwareSpecificationApi.common?.language ?? 'EN';
        this.softwareSpecification.display.graphs = {
            [GraphType.Bar]: softwareSpecificationApi.graphs.BAR_GRAPH,
            [GraphType.Gauge]: softwareSpecificationApi.graphs.ANALOG_METER,
            [GraphType.Exhaust]: softwareSpecificationApi.graphs.EXHAUST_GAS,
        };

        if (softwareSpecificationApi.common.masterClockType == MasterClockType.MasterComm) {
            this.softwareSpecification.clock.master.input = true;
        } else if (softwareSpecificationApi.common.masterClockType == MasterClockType.MasterPulse) {
            this.softwareSpecification.clock.master.hardware = true;
        }
        this.softwareSpecification.clock.utc = softwareSpecificationApi.common.UtcEnable == 1;
    }

    prepareOthersSettings(systemSpecification: SystemSpecification) {
        this.othersSpecification = {
            items: systemSpecification?.items ?? '',
            notes: systemSpecification?.notes ?? '',
        };
    }

    handleGoToTop() {
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    async goToDeviceList() {
        const shouldLeave = await this.shouldLeaveConfirmation();
        if (!shouldLeave) return;
        await this.router.navigateByUrl(`home/${this.vessel.id}/device-list`);
    }

    async shouldLeaveConfirmation() {
        let shouldLeave = true;
        if (this.isSaveEnabled) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'output-settings.AO.cancel.title',
                    backButton: 'output-settings.AO.cancel.cancel',
                    discardButton: 'output-settings.AO.cancel.discard',
                    saveButton: 'output-settings.AO.cancel.save',
                    redDiscard: true,
                },
            });

            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.GO_BACK) shouldLeave = false;
            else if (confirmationOption == ConfirmationDialogOption.SAVE) {
                await this.save();
            } else {
                this.cancel();
            }
        }

        return shouldLeave;
    }

    showMessageDialog(message) {
        this.messageDialogService.openMessage(message);
    }

    get isSaveEnabled(): boolean {
        return (
            this.initialSystemSpecifications.keyboards != this.hardwareSpecification.Keyboard.qty ||
            this.initialSystemSpecifications.trackballs != this.hardwareSpecification.Trackball.qty ||
            this.initialSystemSpecifications.hub != this.hardwareSpecification.HUB.qty ||
            this.initialSystemSpecifications.notes != this.othersSpecification.notes ||
            this.initialSystemSpecifications.items != this.othersSpecification.items
        );
    }

    get isScrollBtnVisible() {
        if (this.tabsContainer) {
            const topContentRect = this.tabsContainer.nativeElement.getBoundingClientRect();
            return topContentRect.y < 0;
        }
        return false;
    }

    static CanDeactivate(component: SystemSpecificationComponent): boolean | Observable<boolean> {
        return from(component.shouldLeaveConfirmation()).pipe(take(1));
    }

    private prepareFuList(fcuUnitList: (FcuUnit & { fuUnitList: FuUnit[] })[]): FuUnitWithFcuIndex[] {
        if (!fcuUnitList?.length) return [];

        return fcuUnitList.flatMap(fcuUnit =>
            (fcuUnit.fuUnitList ?? []).map(fuUnit => ({
                ...fuUnit,
                fcuIndex: fcuUnit.index,
            }))
        );
    }
}
